import { BadgeCheck, Handshake, Megaphone, Truck } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EnquiryForm from "@/components/EnquiryForm";
import ClosingCTA from "@/components/ClosingCTA";
import { FadeUp } from "@/components/Reveal";

const BENEFITS = [
  {
    icon: BadgeCheck,
    title: "A 15-year proven brand",
    copy: "Ranchi already knows, trusts and craves the Taste Ride name.",
  },
  {
    icon: Handshake,
    title: "Two formats to choose",
    copy: "Full dine-in restaurant or a lean, high-margin cloud-kitchen setup.",
  },
  {
    icon: Truck,
    title: "Supply chain & training",
    copy: "Central sourcing, recipes, SOPs and staff training — all included.",
  },
  {
    icon: Megaphone,
    title: "Marketing & app onboarding",
    copy: "Launch support plus Zomato, Swiggy and WhatsApp setup from day one.",
  },
];

export default function Franchisee() {
  return (
    <main data-testid="franchisee-page">
      <PageHeader
        testid="franchisee-header"
        eyebrow="Franchisee Enquiries"
        title="Own a"
        accent="Taste Ride."
        copy="Take a brand Ranchi has loved since 2010 and run it in your city — with our kitchens' recipes, supply chain and delivery playbook behind you."
      />

      <section className="bg-obsidian px-5 pb-24 sm:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4" data-testid="franchisee-benefits">
            {BENEFITS.map((b, i) => (
              <FadeUp key={b.title} delay={i * 0.08}>
                <div className="group h-full rounded-3xl border border-white/10 bg-coal p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-saffron/40">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-saffron/10 text-saffron transition-colors duration-300 group-hover:bg-saffron group-hover:text-obsidian">
                    <b.icon size={22} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-cream">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.copy}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <FadeUp className="lg:col-span-5">
              <span className="font-mono text-xs uppercase tracking-[0.35em] text-saffron">The Next Step</span>
              <h2 className="mt-4 font-display text-2xl font-semibold leading-[1.15] tracking-tight text-cream sm:text-3xl" data-testid="franchisee-form-title">
                Tell us where you want to{" "}
                <span className="font-serif italic font-medium text-saffron">fire up the kitchen.</span>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                Share a few details and our franchise team will call you back with formats,
                investment options and territory availability. No obligation — just a conversation.
              </p>
              <ul className="mt-8 space-y-3">
                {["Territory-first allocation for early partners", "Dine-in & cloud-kitchen formats", "End-to-end launch support"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-flame" />
                    {t}
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.12} className="lg:col-span-7">
              <EnquiryForm type="franchisee" withInvestment testid="franchisee-form" />
            </FadeUp>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </main>
  );
}
